"use client";

import React, { useState } from "react";

import { parsePhoneNumber } from "awesome-phonenumber";

import { useDeviceDetection } from "@/hooks/useDeviceDetection";
import EyeHide from "@/icons/ui/forms/eye-hide.svg";
import EyeShow from "@/icons/ui/forms/eye-show.svg";
import { cn } from "@/utils/cn";

interface PhoneNumberProps {
  phone: string;
  countryCode?: string;
  showIcon?: boolean;
  className?: string;
}

export const PhoneNumber: React.FC<PhoneNumberProps> = ({
  phone,
  countryCode = "US",
  className,
  showIcon = true,
}) => {
  const [showPhone, setShowPhone] = useState(false);
  const { isMobile } = useDeviceDetection();

  const formatPhoneWithHiddenLastSection = (
    phoneNumber: string,
    countryCode: string,
  ) => {
    try {
      const parsedPhone = parsePhoneNumber(phoneNumber, {
        regionCode: countryCode,
      });

      if (!parsedPhone.valid) {
        return phoneNumber;
      }

      const formattedPhone = parsedPhone.number.international;

      // Remove spaces and non-digit characters except parentheses and dashes
      const cleanFormatted = formattedPhone.replace(/[^\+\d\s\-\(\)]/g, "");

      // Split by spaces to get sections
      const sections = cleanFormatted.split(/[\s\-]/);

      if (sections.length > 1) {
        // Hide the last section with asterisks
        const lastSection = sections[sections.length - 1];
        const hiddenSection = "*".repeat(lastSection.length);

        sections[sections.length - 1] = hiddenSection;

        return sections.join(" ");
      }

      return formattedPhone;
    } catch (error) {
      console.error("Error formatting phone number:", error);
      return phoneNumber;
    }
  };

  const formatFullPhone = (phoneNumber: string, countryCode: string) => {
    try {
      const parsedPhone = parsePhoneNumber(phoneNumber, {
        regionCode: countryCode,
      });
      return parsedPhone.valid ? parsedPhone.number.international : phoneNumber;
    } catch (error) {
      console.error("Error formatting phone number:", error);
      return phoneNumber;
    }
  };

  const handlePhoneClick = () => {
    if (isMobile) {
      // On mobile devices, navigate to tel: link
      window.location.href = `tel:${phone}`;
    } else {
      // On desktop devices, toggle showing full phone number
      setShowPhone(!showPhone);
    }
  };

  const handleIconClick = () => {
    setShowPhone(!showPhone);
  };

  const formattedPhone = showPhone
    ? formatFullPhone(phone, countryCode)
    : formatPhoneWithHiddenLastSection(phone, countryCode);
  const normalizePhone = (p: string) => p.replace(/[^\d+]/g, "");

  return (
    <div className={cn("flex items-center text-nowrap gap-3", className)}>
      {showPhone ? (
        <a
          href={`tel:${normalizePhone(formattedPhone)}`}
          className={cn(
            "transition-colors",
            "text-md text-gray-775 hover:text-red",
            isMobile ? "cursor-pointer" : "cursor-pointer hover:text-red",
          )}
          aria-label={`Call ${formattedPhone}`}
        >
          {formattedPhone}
        </a>
      ) : (
        <span className={cn("transition-colors", "text-md text-gray-775")}>
          {formattedPhone}
        </span>
      )}
      {showIcon &&
        (showPhone ? (
          <EyeHide
            className={cn(
              "cursor-pointer transition-opacity duration-100 opacity-100",
            )}
            width={24}
            height={24}
            onClick={handleIconClick}
          />
        ) : (
          <EyeShow
            className={cn(
              "cursor-pointer transition-opacity duration-100 opacity-100",
            )}
            width={24}
            height={24}
            onClick={handleIconClick}
          />
        ))}
    </div>
  );
};
